import CryptoJS from "crypto-js";

// Récupérer la clé de chiffrement depuis les variables d'environnement
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || "default-key-change-in-production";

/**
 * Service de chiffrement/déchiffrement pour sécuriser les données sensibles
 */
export const encryptionService = {
  /**
   * Chiffre une chaîne de caractères
   * @param {string} text - Le texte à chiffrer
   * @returns {string} - Le texte chiffré
   */
  encrypt(text) {
    try {
      if (!text) return "";

      // Chiffrement AES avec la clé d'environnement
      const encrypted = CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
      console.log("🔐 Données chiffrées avec succès");
      return encrypted;
    } catch (error) {
      console.error("❌ Erreur lors du chiffrement:", error);
      return text; // Fallback: retourner le texte non chiffré
    }
  },

  /**
   * Déchiffre une chaîne de caractères
   * @param {string} encryptedText - Le texte chiffré
   * @returns {string} - Le texte déchiffré
   */
  decrypt(encryptedText) {
    try {
      if (!encryptedText) return "";

      // Déchiffrement AES
      const decrypted = CryptoJS.AES.decrypt(encryptedText, ENCRYPTION_KEY);
      const originalText = decrypted.toString(CryptoJS.enc.Utf8);

      if (!originalText) {
        console.warn(
          "⚠️ Impossible de déchiffrer les données - clé incorrecte ou données corrompues"
        );
        return encryptedText; // Fallback: retourner le texte tel quel
      }

      console.log("🔓 Données déchiffrées avec succès");
      return originalText;
    } catch (error) {
      console.error("❌ Erreur lors du déchiffrement:", error);
      return encryptedText; // Fallback: retourner le texte tel quel
    }
  },

  /**
   * Stockage sécurisé dans localStorage
   * @param {string} key - La clé de stockage
   * @param {string} value - La valeur à stocker (sera chiffrée)
   */
  setSecureItem(key, value) {
    try {
      const encryptedValue = this.encrypt(value);
      localStorage.setItem(key, encryptedValue);
      console.log(`🔐 Stockage sécurisé: ${key}`);
    } catch (error) {
      console.error("❌ Erreur lors du stockage sécurisé:", error);
      localStorage.setItem(key, value); // Fallback
    }
  },

  /**
   * Récupération sécurisée depuis localStorage
   * @param {string} key - La clé de stockage
   * @returns {string} - La valeur déchiffrée
   */
  getSecureItem(key) {
    try {
      const encryptedValue = localStorage.getItem(key);
      if (!encryptedValue) return null;

      const decryptedValue = this.decrypt(encryptedValue);
      console.log(`🔓 Récupération sécurisée: ${key}`);
      return decryptedValue;
    } catch (error) {
      console.error("❌ Erreur lors de la récupération sécurisée:", error);
      return localStorage.getItem(key); // Fallback
    }
  },

  /**
   * Stockage sécurisé dans sessionStorage
   * @param {string} key - La clé de stockage
   * @param {string} value - La valeur à stocker (sera chiffrée)
   */
  setSecureSessionItem(key, value) {
    try {
      const encryptedValue = this.encrypt(value);
      sessionStorage.setItem(key, encryptedValue);
      console.log(`🔐 Stockage session sécurisé: ${key}`);
    } catch (error) {
      console.error("❌ Erreur lors du stockage session sécurisé:", error);
      sessionStorage.setItem(key, value); // Fallback
    }
  },

  /**
   * Récupération sécurisée depuis sessionStorage
   * @param {string} key - La clé de stockage
   * @returns {string} - La valeur déchiffrée
   */
  getSecureSessionItem(key) {
    try {
      const encryptedValue = sessionStorage.getItem(key);
      if (!encryptedValue) return null;

      const decryptedValue = this.decrypt(encryptedValue);
      console.log(`🔓 Récupération session sécurisée: ${key}`);
      return decryptedValue;
    } catch (error) {
      console.error("❌ Erreur lors de la récupération session sécurisée:", error);
      return sessionStorage.getItem(key); // Fallback
    }
  },

  /**
   * Suppression sécurisée
   * @param {string} key - La clé à supprimer
   * @param {boolean} fromSession - True pour sessionStorage, false pour localStorage
   */
  removeSecureItem(key, fromSession = false) {
    try {
      if (fromSession) {
        sessionStorage.removeItem(key);
        console.log(`🗑️ Suppression session sécurisée: ${key}`);
      } else {
        localStorage.removeItem(key);
        console.log(`🗑️ Suppression sécurisée: ${key}`);
      }
    } catch (error) {
      console.error("❌ Erreur lors de la suppression sécurisée:", error);
    }
  },

  /**
   * Vérifie si la clé de chiffrement est celle par défaut (non sécurisée)
   * @returns {boolean} - True si la clé par défaut est utilisée
   */
  isUsingDefaultKey() {
    return ENCRYPTION_KEY === "default-key-change-in-production";
  },

  /**
   * Génère une nouvelle clé de chiffrement aléatoire
   * @returns {string} - Nouvelle clé de chiffrement
   */
  generateNewKey() {
    return CryptoJS.lib.WordArray.random(256 / 8).toString();
  },
};

// Avertissement de sécurité en développement
if (process.env.NODE_ENV === "development" && encryptionService.isUsingDefaultKey()) {
  console.warn(
    "⚠️ SÉCURITÉ: Vous utilisez la clé de chiffrement par défaut. Changez REACT_APP_ENCRYPTION_KEY en production !"
  );
  console.log("💡 Générez une nouvelle clé:", encryptionService.generateNewKey());
}

export default encryptionService;
